import { useCallback, useEffect, useState } from "react";
import expenseService from "../services/expenseService";
import notify from "../../../services/notify";
import useDebounce from "../../../hooks/useDebounce";

const PAGE_SIZE = 20;

const useExpenses = () => {

    const [expenses, setExpenses] = useState([]);

    const [page, setPage] = useState(1);

    const [loading, setLoading] = useState(true);

    const [loadingMore, setLoadingMore] = useState(false);

    const [hasMore, setHasMore] = useState(true);

    const [error, setError] = useState(null);

    //----------------------------------------------------

    const [

        summary,
    
        setSummary,
    
    ] = useState({
    
        totalCount: 0,
    
        totalAmount: 0,
    
    });

    //----------------------------------------------------

    const [search, setSearch] = useState("");

    const debouncedSearch = useDebounce(
    
        search,
    
        500
    
    );

    //----------------------------------------------------
    
    const [filters, setFilters] = useState({
    
        carId: "",
    
        categoryId: "",
    
        fromDate: "",
    
        toDate: "",
    
    });

    //----------------------------------------------------

    const loadPage = useCallback(async (pageNumber) => {

        const result = await expenseService.getExpenses({

            page: pageNumber,

            pageSize: PAGE_SIZE,

            search: debouncedSearch,

            carId: filters.carId,
        
            categoryId: filters.categoryId,
        
            fromDate: filters.fromDate,
        
            toDate: filters.toDate,

        });

        setSummary({

            totalCount: result.totalCount,
        
            totalAmount: result.totalAmount,
        
        });

        return result.expenses ?? [];

    }, [

        debouncedSearch,

        filters,
    
    ]);

    //----------------------------------------------------

    const updateFilters = useCallback(

        (newFilters) => {
    
            setFilters(previous => ({
    
                ...previous,
    
                ...newFilters,
    
            }));

            setPage(1);
    
        },
    
        []
    
    );

    //----------------------------------------------------

    const loadFirstPage = useCallback(async () => {

        try {

            setLoading(true);

            setError(null);

            const data = await loadPage(1);

            setExpenses(data);

            setPage(1);

            setHasMore(data.length === PAGE_SIZE);

        }

        catch (err) {

            console.error(err);

            setError(err);

            notify.error(err);

        }

        finally {

            setLoading(false);

        }

    }, [  
        loadPage,
    ]);

    //----------------------------------------------------

    const loadMore = useCallback(async () => {

        if (loadingMore || !hasMore) {

            return;

        }

        try {

            setLoadingMore(true);

            const nextPage = page + 1;

            const data = await loadPage(nextPage);

            setExpenses(previous => {

                const ids = new Set(
            
                    previous.map(
            
                        x => x.id
            
                    )
            
                );
            
                const merged = [
            
                    ...previous,
            
                    ...data.filter(
            
                        x => !ids.has(x.id)
            
                    ),
            
                ];
            
                return merged;
            
            });

            setPage(nextPage);

            setHasMore(data.length === PAGE_SIZE);

        }

        catch (err) {

            console.error(err);

            notify.error(err);

        }

        finally {

            setLoadingMore(false);

        }

    }, [

        page,

        hasMore,

        loadingMore,

        loadPage,

    ]);

    //----------------------------------------------------

    const refresh = useCallback(() => {

        loadFirstPage();

    }, [loadFirstPage]);

    //----------------------------------------------------

useEffect(() => {

    loadFirstPage();

}, [

    loadFirstPage,

]);

    //----------------------------------------------------

    return {

        expenses,

        loading,

        loadingMore,

        hasMore,

        error,

        refresh,

        loadMore,

        search,

        setSearch,
    
        filters,
    
        updateFilters,

        summary,

    };

};

export default useExpenses;